/**
 * Server-Sent Events (SSE) client for real-time notifications.
 * Auth is the httpOnly cookie (EventSource cannot set Authorization).
 */
import { convertApiNotification } from '../contexts/NotificationContext'

export interface SSENotificationEvent {
  type: 'connected' | 'notification' | 'error'
  message?: string
  data?: any
  unread_count?: number
}

export class SSEClient {
  private eventSource: EventSource | null = null
  private reconnectAttempts = 0
  private maxReconnectAttempts = 5
  private reconnectDelay = 1000
  private listeners: Map<string, Set<(event: SSENotificationEvent) => void>> = new Map()

  constructor(
    private onNotification?: (notification: any) => void,
    private onUnreadCountChange?: (count: number) => void,
    private onError?: (error: Error) => void
  ) {}

  connect(): void {
    if (this.eventSource && this.eventSource.readyState !== EventSource.CLOSED) {
      return // Already connected
    }

    const apiBase = process.env.REACT_APP_API_URL || `http://${window.location.hostname}:8000`
    const url = `${apiBase.replace(/\/+$/, '')}/api/notifications/stream/`

    this.eventSource = new EventSource(url, {
      withCredentials: true,
    })

    this.eventSource.onopen = () => {
      this.reconnectAttempts = 0
      console.log('SSE connection opened')
    }

    this.eventSource.onmessage = (event) => {
      try {
        const data: SSENotificationEvent = JSON.parse(event.data)
        this.handleMessage(data)
      } catch (error) {
        console.error('Failed to parse SSE message:', error)
      }
    }

    this.eventSource.onerror = (error) => {
      console.error('SSE connection error:', error)
      
      if (this.eventSource?.readyState === EventSource.CLOSED) {
        this.handleReconnect()
      }
      
      if (this.onError) {
        this.onError(new Error('SSE connection error'))
      }
    }
  }

  private handleMessage(data: SSENotificationEvent): void {
    switch (data.type) {
      case 'connected':
        console.log('SSE connected:', data.message)
        if (data.unread_count !== undefined && this.onUnreadCountChange) {
          this.onUnreadCountChange(data.unread_count)
        }
        break

      case 'notification':
        if (data.data) {
          const notification = convertApiNotification(data.data)
          if (this.onNotification) {
            this.onNotification(notification)
          }
          if (data.unread_count !== undefined && this.onUnreadCountChange) {
            this.onUnreadCountChange(data.unread_count)
          }
        }
        break

      case 'error':
        console.error('SSE error:', data.message)
        if (this.onError) {
          this.onError(new Error(data.message || 'Unknown SSE error'))
        }
        break
    }

    // Notify listeners
    const listeners = this.listeners.get(data.type)
    if (listeners) {
      listeners.forEach(listener => listener(data))
    }
  }

  private handleReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Max reconnect attempts reached')
      return
    }

    this.reconnectAttempts++
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1) // Exponential backoff

    console.log(`Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`)

    setTimeout(() => {
      this.disconnect()
      this.connect()
    }, delay)
  }

  disconnect(): void {
    if (this.eventSource) {
      this.eventSource.close()
      this.eventSource = null
    }
  }

  addEventListener(type: string, listener: (event: SSENotificationEvent) => void): void {
    if (!this.listeners.has(type)) {
      this.listeners.set(type, new Set())
    }
    this.listeners.get(type)!.add(listener)
  }

  removeEventListener(type: string, listener: (event: SSENotificationEvent) => void): void {
    const listeners = this.listeners.get(type)
    if (listeners) {
      listeners.delete(listener)
    }
  }

  isConnected(): boolean {
    return this.eventSource !== null && this.eventSource.readyState === EventSource.OPEN
  }
}
